import { superAdminApi } from "../authService";
import { ApiResponse, PaginatedApiResponse, PaginationParams } from "../../types/api";
import {
  SupportConversation,
  SupportConversationMessage,
  SupportConversationTicketCategory,
} from "../supportConversationService";

const RESOURCE = "/super-admin/support/conversations";

export interface AdminSupportConversationSummary {
  queued_human: number;
  assigned: number;
  open_ai: number;
  closed?: number;
  with_ticket?: number;
  sla_suggested_open?: number;
  avg_ai_turns_closed?: number | null;
}

export const listAdminSupportConversations = async (
  params?: PaginationParams & {
    status?: string;
    channel?: string;
    tenant_id?: number;
    search?: string;
  }
): Promise<PaginatedApiResponse<SupportConversation>> => {
  const response = await superAdminApi.get<
    PaginatedApiResponse<SupportConversation>
  >(RESOURCE, { params });

  return response.data;
};

export const getAdminSupportConversation = async (
  id: number
): Promise<ApiResponse<SupportConversation>> => {
  const response = await superAdminApi.get<ApiResponse<SupportConversation>>(
    `${RESOURCE}/${id}`
  );

  return response.data;
};

export const getAdminSupportConversationSummary = async (): Promise<
  ApiResponse<AdminSupportConversationSummary>
> => {
  const response = await superAdminApi.get<
    ApiResponse<AdminSupportConversationSummary>
  >(`${RESOURCE}/summary`);

  return response.data;
};

export const assumeAdminSupportConversation = async (
  id: number
): Promise<ApiResponse<SupportConversation>> => {
  const response = await superAdminApi.post<ApiResponse<SupportConversation>>(
    `${RESOURCE}/${id}/assume`,
    {}
  );

  return response.data;
};

export const replyAdminSupportConversation = async (
  id: number,
  body: string
): Promise<
  ApiResponse<{
    message: SupportConversationMessage;
    conversation: SupportConversation;
  }>
> => {
  const response = await superAdminApi.post<
    ApiResponse<{
      message: SupportConversationMessage;
      conversation: SupportConversation;
    }>
  >(`${RESOURCE}/${id}/messages`, { body });

  return response.data;
};

export const closeAdminSupportConversation = async (
  id: number
): Promise<ApiResponse<SupportConversation>> => {
  const response = await superAdminApi.post<ApiResponse<SupportConversation>>(
    `${RESOURCE}/${id}/close`,
    {}
  );

  return response.data;
};

export const syncAdminSupportKnowledge = async (
  locale?: string
): Promise<
  ApiResponse<{ upserted: number; skipped: number; deleted: number }>
> => {
  const response = await superAdminApi.post<
    ApiResponse<{ upserted: number; skipped: number; deleted: number }>
  >("/super-admin/support/knowledge/sync", locale ? { locale } : {});

  return response.data;
};

export const openAdminSupportConversationTicket = async (
  id: number,
  category: SupportConversationTicketCategory
): Promise<
  ApiResponse<{
    conversation: SupportConversation;
    ticket: {
      id: number;
      status: string;
      subject: string;
      category?: string;
    };
  }>
> => {
  const response = await superAdminApi.post<
    ApiResponse<{
      conversation: SupportConversation;
      ticket: {
        id: number;
        status: string;
        subject: string;
        category?: string;
      };
    }>
  >(`${RESOURCE}/${id}/open-ticket`, { category });

  return response.data;
};
